import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { createPortal } from "react-dom";
import Brand from "./Brand.jsx";
import GatedLink from "./GatedLink.jsx";
import WalletConnect from "./WalletConnect.jsx";

const navItems = [
    // The "Run the Numbers" calculator that backed the Protocol Structure
    // page now lives at the bottom of Home, so the standalone page was
    // retired (Jun 2026) along with this nav link.
    { to: "/protocol-health", label: "Protocol Health", gated: false },
    { to: "/faq", label: "FAQ", gated: false }
];

/**
 * Public-facing top navigation. Dark green strip with the GILDer wordmark
 * on the left, marketing links in the centre (lg+ only), and the yellow
 * Connect Wallet CTA. On screens below lg the links collapse behind a
 * hamburger that opens a slide-in side sheet.
 */
export default function TopNav() {
    const [menuOpen, setMenuOpen] = useState(false);
    return (
        <header className="bg-vb-dark text-white">
            <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 py-4 sm:gap-6 sm:px-8">
                <Link to="/" aria-label="GILDer home" className="shrink-0">
                    <Brand tone="light" />
                </Link>

                <nav className="hidden items-center gap-8 lg:flex">
                    {navItems.map((item, idx) =>
                        item.gated ? (
                            <GatedLink
                                key={`${item.label}-${idx}`}
                                to={item.to}
                                className="text-sm font-medium text-white hover:text-vb-accent transition-colors"
                            >
                                {item.label}
                            </GatedLink>
                        ) : (
                            <NavLink
                                key={`${item.label}-${idx}`}
                                to={item.to}
                                end={item.to === "/"}
                                className="text-sm font-medium text-white hover:text-vb-accent transition-colors"
                            >
                                {item.label}
                            </NavLink>
                        )
                    )}
                </nav>

                <div className="flex items-center gap-3 sm:gap-4">
                    <WalletConnect />
                    <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-vb-accent hover:bg-white/10 lg:hidden"
                        onClick={() => setMenuOpen(true)}
                        aria-label="Open menu"
                    >
                        <svg
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            aria-hidden="true"
                        >
                            <line x1="3" y1="6" x2="17" y2="6" />
                            <line x1="3" y1="10" x2="17" y2="10" />
                            <line x1="3" y1="14" x2="17" y2="14" />
                        </svg>
                    </button>
                </div>
            </div>

            <PublicMobileSheet open={menuOpen} onClose={() => setMenuOpen(false)} />
        </header>
    );
}

function PublicMobileSheet({ open, onClose }) {
    if (!open) return null;
    return createPortal(
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
            <aside className="relative ml-auto flex h-full w-72 max-w-[85%] flex-col bg-vb-dark p-6 text-white shadow-2xl">
                <div className="flex items-center justify-between">
                    <Brand tone="light" />
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close menu"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-white/10"
                    >
                        <svg
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            aria-hidden="true"
                        >
                            <line x1="5" y1="5" x2="15" y2="15" />
                            <line x1="15" y1="5" x2="5" y2="15" />
                        </svg>
                    </button>
                </div>
                <nav className="mt-8 flex flex-col gap-2">
                    {navItems.map((item, idx) =>
                        item.gated ? (
                            <GatedLink
                                key={`${item.label}-${idx}`}
                                to={item.to}
                                onClick={onClose}
                                className="rounded-xl px-3 py-3 text-sm font-medium text-white hover:bg-white/10"
                            >
                                {item.label}
                            </GatedLink>
                        ) : (
                            <NavLink
                                key={`${item.label}-${idx}`}
                                to={item.to}
                                end={item.to === "/"}
                                onClick={onClose}
                                className="rounded-xl px-3 py-3 text-sm font-medium text-white hover:bg-white/10"
                            >
                                {item.label}
                            </NavLink>
                        )
                    )}
                </nav>
            </aside>
        </div>,
        document.body
    );
}
